import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

import { ServicesManager } from '@core';
import { DicomPrintConfigType, FilmSize, PrintDicomConfig } from '@core/services/DicomPrintService/types';
import { useGetDisplaysetModality, useLocalforage } from '@hooks';
export const DICOM_PRINT_CONFIG_KEY = 'dicomPrintConfig';
type DicomPrintAction =
	| {
			type: 'UPDATE_ORIENTATION';
			payload: { data: Partial<DicomPrintConfigType['filmOrientation']>; callback: (DicomPrintConfigType) => void };
	  }
	| { type: 'UPDATE_DPI'; payload: { data: Partial<DicomPrintConfigType['dpi']>; callback: (DicomPrintConfigType) => void } }
	| { type: 'UPDATE_FILM_SIZE'; payload: { data: Partial<DicomPrintConfigType['filmSize']>; callback: (DicomPrintConfigType) => void } }
	| {
			type: 'UPDATE_DICOM_PRINTER';
			payload: { data: Partial<DicomPrintConfigType['dicomPrinterID']>; callback: (DicomPrintConfigType) => void };
	  }
	| { type: 'RESET_CONFIG' }
	| { type: 'TOGGLE_PRINT_DICOM_MODE'; payload: DicomPrintConfigType };

const DEFAULT_STATE: DicomPrintConfigType = {
	dicomPrinterID: null,
	filmOrientation: 'LANDSCAPE',
	filmSize: '10INX12IN',
	dpi: 300,
	imagePixelSizeFromFilm: { widthPx: 3600, heightPx: 3000 },
	endablePrintDicomMode: false,
};

/**
 * Context Value
 */
type DicomPrintContextValues = {
	state: typeof DEFAULT_STATE;
	getState: () => typeof DEFAULT_STATE;
	reset: () => void;
	getImagePixelSizeFromFilm: (props: Pick<DicomPrintConfigType, 'filmSize' | 'filmOrientation' | 'dpi'>) => {
		widthPx: number;
		heightPx: number;
	};
	getViewportRatio: (filmSize: FilmSize) => number;
	setPrinter: (printer: number) => void;
	setOrientation: (orientation: string) => void;
	setFilmSize: (filmSize: string) => void;
	setDpi: (dpi: number) => void;
	toggleDicomPrintMode: () => void;
};

export const DicomPrintContext = createContext<DicomPrintContextValues>({
	state: DEFAULT_STATE,
	getState: null,
	reset: null,
	getImagePixelSizeFromFilm: null,
	getViewportRatio: null,
	setOrientation: null,
	setPrinter: null,
	setFilmSize: null,
	setDpi: null,
	toggleDicomPrintMode: null,
});

/**
 * Provider Component
 */
function DicomPrintProvider({ children, servicesManager }: { children: React.ReactNode; servicesManager: ServicesManager }) {
	const { dicomPrintService } = servicesManager.services;
	const dicomPrintReducer = useCallback(
		(state: DicomPrintConfigType, action: DicomPrintAction): typeof DEFAULT_STATE => {
			const { getImagePixelSizeFromFilm } = dicomPrintService;
			switch (action.type) {
				case 'TOGGLE_PRINT_DICOM_MODE':
					return { ...action.payload, endablePrintDicomMode: !state.endablePrintDicomMode };
				case 'RESET_CONFIG':
					return { ...DEFAULT_STATE, endablePrintDicomMode: state.endablePrintDicomMode };
				case 'UPDATE_DICOM_PRINTER': {
					const newState = { ...state, dicomPrinterID: action.payload.data };
					action.payload.callback(newState);
					return newState;
				}
				case 'UPDATE_DPI': {
					const newImagePx = getImagePixelSizeFromFilm({
						dpi: action.payload.data,
						filmOrientation: state.filmOrientation,
						filmSize: state.filmSize,
					});
					const newState = { ...state, dpi: action.payload.data, imagePixelSizeFromFilm: newImagePx };
					action.payload.callback(newState);
					return newState;
				}
				case 'UPDATE_FILM_SIZE': {
					const newImagePx = getImagePixelSizeFromFilm({
						dpi: state.dpi,
						filmOrientation: state.filmOrientation,
						filmSize: action.payload.data,
					});
					const newState = { ...state, filmSize: action.payload.data, imagePixelSizeFromFilm: newImagePx };
					action.payload.callback(newState);
					return newState;
				}
				case 'UPDATE_ORIENTATION': {
					const newImagePx = getImagePixelSizeFromFilm({
						dpi: state.dpi,
						filmOrientation: action.payload.data,
						filmSize: state.filmSize,
					});
					const newState = { ...state, filmOrientation: action.payload.data, imagePixelSizeFromFilm: newImagePx };
					action.payload.callback(newState);
					return newState;
				}
				default:
					return state;
			}
		},
		[dicomPrintService],
	);
	const modality = useGetDisplaysetModality(servicesManager);
	const [dicomPrintConfig, setDicomPrintConfig] = useLocalforage<PrintDicomConfig>(DICOM_PRINT_CONFIG_KEY, {});
	const defaultPrintConfigByModality = useMemo(() => {
		if (modality && dicomPrintConfig && dicomPrintConfig[modality]) {
			return dicomPrintConfig[modality];
		}
		return DEFAULT_STATE;
	}, [dicomPrintConfig, modality]);

	const [state, dispatch] = useReducer(dicomPrintReducer, defaultPrintConfigByModality);
	const getState = useCallback(() => {
		return state;
	}, [state]);

	const setDicomPrintConfigToStorage = useCallback(
		(newConfig: DicomPrintConfigType) => {
			if (modality) {
				setDicomPrintConfig({
					...dicomPrintConfig,
					[modality]: newConfig,
				});
			}
		},
		[modality],
	);

	const setDpi = useCallback(
		(dpi: DicomPrintConfigType['dpi']) => {
			dispatch({
				type: 'UPDATE_DPI',
				payload: { callback: setDicomPrintConfigToStorage, data: dpi },
			});
		},
		[setDicomPrintConfigToStorage],
	);

	const setFilmSize = useCallback(
		(filmSize: DicomPrintConfigType['filmSize']) => {
			dispatch({
				type: 'UPDATE_FILM_SIZE',
				payload: { callback: setDicomPrintConfigToStorage, data: filmSize },
			});
		},
		[setDicomPrintConfigToStorage],
	);

	const setPrinter = useCallback(
		(printer: DicomPrintConfigType['dicomPrinterID']) => {
			dispatch({
				type: 'UPDATE_DICOM_PRINTER',
				payload: { callback: setDicomPrintConfigToStorage, data: printer },
			});
		},
		[setDicomPrintConfigToStorage],
	);

	const setOrientation = useCallback(
		(filmOrientation: DicomPrintConfigType['filmOrientation']) => {
			dispatch({
				type: 'UPDATE_ORIENTATION',
				payload: { callback: setDicomPrintConfigToStorage, data: filmOrientation },
			});
		},
		[setDicomPrintConfigToStorage],
	);
	const reset = useCallback(
		() =>
			dispatch({
				type: 'RESET_CONFIG',
			}),
		[setDicomPrintConfigToStorage],
	);
	const toggleDicomPrintMode = useCallback(
		() =>
			dispatch({
				type: 'TOGGLE_PRINT_DICOM_MODE',
				payload: defaultPrintConfigByModality,
			}),
		[defaultPrintConfigByModality],
	);
	useEffect(() => {
		if (dicomPrintService) {
			dicomPrintService.setServiceImplementation({
				getState,
				reset,
				setDpi,
				setFilmSize,
				setPrinter,
				setOrientation,
				toggleDicomPrintMode,
			});
		}
	}, [getState, reset, setDpi, setFilmSize, dicomPrintService, setOrientation, setPrinter, toggleDicomPrintMode]);

	const value = useMemo(
		() => ({
			state,
			getState,
			reset,
			setDpi,
			setFilmSize,
			setPrinter,
			setOrientation,
			getImagePixelSizeFromFilm: dicomPrintService.getImagePixelSizeFromFilm,
			getViewportRatio: dicomPrintService.getViewportRatio,
			toggleDicomPrintMode,
		}),
		[
			getState,
			reset,
			state,
			setDpi,
			setFilmSize,
			setPrinter,
			dicomPrintService.getImagePixelSizeFromFilm,
			dicomPrintService.getViewportRatio,
			setOrientation,
			toggleDicomPrintMode,
		],
	);

	return <DicomPrintContext.Provider value={value}>{children}</DicomPrintContext.Provider>;
}

DicomPrintProvider.propTypes = {
	children: PropTypes.any,
};

export const useDicomPrint = () => useContext(DicomPrintContext);
export default DicomPrintProvider;
